import { fromZonedTime } from 'date-fns-tz';

import { CandidateOriginalInput, DeclineFeedbackFormData, SendMessageRequest } from '../types';

/**
 * Converts form data to API payload format.
 * Transforms reason input form data to originalInput for the backend.
 */
export default function declineFeedbackFormDataPayloadToApiPayload(
    { reasonInput, ...rest }: DeclineFeedbackFormData,
    timeZone: string
): SendMessageRequest['payload'] {
    const originalInput = mapReasonInputToOriginalInput(reasonInput, timeZone);

    return {
        ...rest,
        originalInput: originalInput ?? undefined
    };
}

/**
 * Maps reason input form data to originalInput format.
 * Returns undefined if no valid input data is present.
 */
function mapReasonInputToOriginalInput(
    reasonInput: DeclineFeedbackFormData['reasonInput'],
    timeZone: string
): CandidateOriginalInput | undefined {
    if (!reasonInput) {
        return undefined;
    }

    switch (reasonInput.type) {
        case 'availability':
            if (!reasonInput.value?.[0] || !reasonInput.value?.[1]) {
                return undefined;
            }

            return {
                availabilityStartDateUtc: fromZonedTime(reasonInput.value[0], timeZone).toISOString(),
                availabilityEndDateUtc: fromZonedTime(reasonInput.value[1], timeZone).toISOString()
            };

        case 'location':
            if (!reasonInput.locationKey.placeId && !reasonInput.locationKey.text) {
                return undefined;
            }

            return {
                locationKey: {
                    placeId: reasonInput.locationKey.placeId,
                    text: reasonInput.locationKey.text
                }
            };

        case 'role': {
            // A valid combination is role-only, speciality-only, or both.
            const roleId = reasonInput.role?.id;
            const specialtyId = reasonInput.specialty?.id;

            if (!roleId && !specialtyId) {
                return undefined;
            }

            return {
                ...(roleId ? { roleId } : {}),
                ...(specialtyId ? { specialtyId } : {})
            };
        }

        case 'skills':
            if (reasonInput.skills.length === 0) {
                return undefined;
            }

            return {
                skillIds: reasonInput.skills.map(skill => skill.id)
            };

        default:
            return undefined;
    }
}
